<script setup lang="ts">
const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;

definePageMeta({
  layout: "static",
});

useSeoMeta({
  title: "Modern Slavery Act Transparency Statement | &Dine",
  description:
    "&Dine's Modern Slavery Act Transparency Statement. Our commitment to preventing slavery and human trafficking in our business and supply chains.",
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/modern-slavery` }],
});

const { staticUrl } = useImageUrl();
</script>

<template>
  <section class="modern-slavery">
    <h1 class="grey-text">Modern Slavery Act Transparency Statement for &amp;Dine Ltd</h1>
    <p><br /></p>
    <p><strong>Introduction from Max Bennett, Director of &amp;Dine Ltd</strong></p>
    <p>
      At &amp;Dine Ltd, we are dedicated to supporting human rights and take our responsibilities
      under the Modern Slavery Act 2015 very seriously. We adopt a zero-tolerance approach to
      slavery and human trafficking, committed to understanding the risks to ensure that there is no
      modern slavery in our business or supply chains.
    </p>
    <p><br /></p>
    <p><strong>Structure of &amp;Dine Ltd</strong></p>
    <p>
      &amp;Dine Ltd is a prominent player in the digital food marketplace, catering primarily to
      promoting independent food businesses in London to business customers. The company operates a
      dynamic platform that hosts numerous food vendors and partners, with a strong focus on
      inclusivity and accessibility in the food industry.
    </p>
    <p><br /></p>
    <p><strong>Our Supply Chains</strong></p>
    <p>
      In the hospitality and digital marketplace sectors, our suppliers are categorized into two
      main groups: food suppliers and support suppliers. Food suppliers provide all necessary goods
      from ingredients to packaging, while support suppliers furnish &amp;Dine Ltd with technology,
      professional services, and consultancy essential for our operations. We also work with various
      partners and vendors who contribute significantly to our service offerings.
    </p>
    <p><br /></p>
    <p><strong>Our Anti-slavery Initiatives and Policies</strong></p>
    <p>
      Our approach to managing modern slavery is orchestrated through our Safety, Security, and Risk
      Management Committee, which actively monitors and evaluates our practices in this area to
      drive continuous improvement. Our commitment is mirrored in our training programs and
      corporate policies, demanding the highest ethical standards from our teams and suppliers
      alike.
    </p>
    <p>
      We strongly encourage our employees to report any ethical concerns, supported by our
      Whistleblowing Policy that includes specific provisions regarding modern slavery and enables
      confidential reporting in multiple languages. We are dedicated to ensuring that every employee
      can raise concerns without fear of retaliation.
    </p>
    <p><br /></p>
    <p><strong>Training</strong></p>
    <p>
      Awareness and training are key components of our strategy to prevent modern slavery. Targeted
      training sessions are conducted for staff in high-risk roles and general training for all
      employees to ensure they understand what modern slavery is, recognize its signs, and know how
      to report concerns effectively.
    </p>
    <p><br /></p>
    <p><strong>Due Diligence Processes for Slavery and Human Trafficking</strong></p>
    <p>
      We assess areas within our business and supply chains that might pose risks of modern slavery
      and human trafficking. Our central procurement process aids in maintaining transparency with
      our suppliers and understanding risk areas better. For new suppliers or those in higher-risk
      categories, we perform due diligence and incorporate specific contractual clauses to ensure
      adherence to our standards against modern slavery and human trafficking.
    </p>
    <p><br /></p>
    <p><strong>Further Steps</strong></p>
    <p>
      While &amp;Dine Ltd has made significant progress in combating modern slavery and human
      trafficking, we acknowledge the need for ongoing vigilance and improvement. These include:
    </p>
    <ul>
      <li>Monitoring updates to the Modern Slavery Act.</li>
      <li>Continuously assessing new and existing high-risk suppliers.</li>
      <li>Regularly updating our training programs to remain effective.</li>
    </ul>
    <p>
      We are committed to being a responsible business and continuously evolving our strategies to
      fight modern slavery is a key part of this commitment. This statement is made pursuant to
      section 54(1) of the Modern Slavery Act 2015 and constitutes our slavery and human trafficking
      statement for the financial year ending 31 December 2023. This statement has been approved by
      the board of directors of &amp;Dine Ltd, who will review and update it annually.
    </p>
    <p><br /></p>
    <p><strong>Signature of Director:</strong></p>
    <img
      loading="lazy"
      :src="staticUrl('/Images/site-images/max-signature.png')"
      width="200"
      alt="signature"
    />
    <p><strong>Name of Director: Max Bennett</strong></p>
    <p><strong>Date: 21/04/2024</strong></p>
    <p><br /></p>
  </section>
</template>

<style lang="scss">
.modern-slavery {
  width: 90%;
  padding: 50px 5%;
  background-color: var(--color-white-1);
  margin-top: 80px;
  p,
  li {
    color: var(--color-grey-1);
  }
}
</style>
